/**
 * Canonical `component` values for page.data[sectionSlug].component (travel-buildable-empty layout).
 * Page sections prefer buildable-*. Blocks inside an elastic section stay editable-*.
 */
import { normalizeAgentSyncSectionRow } from '@/utils/websitePageAgentI18n'

export {
	AGENT_HTML_ELASTIC_COMPONENT_ID,
	ELASTIC_SECTION_COMPONENT_IDS,
	ELASTIC_INNER_COMPONENT_IDS,
	normalizeElasticComponentType,
	isAllowedElasticComponentType,
	resolveStoredElasticComponent,
} from './elasticComponentTypes'

/**
 * Frontend-only tools for the website page editor (ap.orchestrator lists them; execution runs here).
 * Pass a factory `getEditorApi` that returns the live editor API object (or null when unavailable).
 *
 * @param {() => object|null} getEditorApi
 * @returns {Array<{ name: string, description: string, schema: object, execute: function }>}
 */
export function createWebsitePageAgentTools(getEditorApi) {
	function getApi() {
		return typeof getEditorApi === 'function' ? getEditorApi() : null
	}

	return [
		{
			name: 'website_page_get_state',
			description: [
				'Read the current page draft for the visual editor: layout, **stylesheet** (user page CSS),',
				'**websitePrimaryColor** / **websiteSecondaryColor** (hub theme from website settings),',
				'elastic section order, per-section metadata, and for **buildable-agent-html** sections a truncated `htmlPreview`',
				'(when **snapshotCompact** / automatic context), **`htmlPreviewTruncated`** when abbreviated,',
				'plus `htmlLength`, **`agentCssPreview`** / **`agentCssLength`** for per-section AI CSS (`page.data[id].agentCss`),',
				(
					'and **`i18nFieldIds`** / **`i18nLocalesSample`** when an `i18n` blob is stored on the section '
					+ '(required for correct text editing when HTML uses **`data-i18n-field`**),'
				),
				'and **`i18nImageFieldIds`** when the HTML uses **`data-i18n-image`** on `<img>` (click-to-replace in the builder).',
				(
					'**`i18nAgentHrefOnlyFieldIds`** lists bases in **`data-i18n-href-field="<base>"`**; URL is **`base-href`** in `i18n`; '
					+ '**`i18nAgentCtaFieldIds`** lists bases for **`<a data-i18n-cta … data-i18n-field="<base>">`** (URL **`base-href`**).'
				),
				'Server-side tool stubs are placeholders; **currentState** in the chat request and this',
				'tool result after client execution are authoritative.',
				'Call before proposing edits when you need exact slugs or to diff prior HTML/CSS.',
			].join(' '),
			schema: {},
			async execute() {
				const api = getApi()
				if (!api) {
					return { state: null, error: 'editor_not_ready' }
				}
				return { state: api.getSnapshot() }
			},
		},
		{
			name: 'website_page_save_draft',
			description: [
				'Persist the current in-memory page draft to the hub (silent save, same as Save without toast).',
				'Call **only** when the user explicitly asks to save or persist the page to the server.',
				'Do **not** call after normal assistant edits — the builder preview updates live via websocket without saving.',
			].join(' '),
			schema: {},
			async execute() {
				const api = getApi()
				if (!api) {
					return { saved: false, error: 'editor_not_ready' }
				}
				await api.saveDraftSilent()
				return { saved: true }
			},
		},
		{
			name: 'website_page_sync_agent_layout',
			description: [
				'**Required for any on-canvas change** (new sections, cards, grids, seasonal blocks, headings, or layout).',
				'Chat text alone does **not** update the builder preview — you must call this tool with real HTML (and CSS when needed).',
				'Apply **AI-generated layout** using elastic sections of type **buildable-agent-html** only.',
				'**Per-section CSS:** optional **`agentCss`** on each `sections` entry is stored on `page.data[id].agentCss` and injected',
				'in the preview with a wrapper class **`agent-html-scope--<id>`** — scope selectors under that class (e.g. ',
				'`.agent-html-scope--hero .title { … }`). Omit **`html`** or **`agentCss`** keys on an entry to leave that field unchanged',
				'(minimal patches). Use **`agentCss: null`** to clear section CSS.',
				(
					'**Global page CSS:** when you pass `stylesheet`, the page `stylesheet` field is set to that string '
					+ '(replaces previous custom CSS from this field). Omit the property entirely to leave page CSS unchanged.'
				),
				'**HTML:** when you pass `sections`, each entry becomes one elastic block:',
				'`page.data[ id ].component` = buildable-agent-html,',
				'`page.data[ id ].html` = fragment HTML (no full document; use semantic tags and scoped classes, e.g. prefix with',
				'`agent-` for class names).',
				(
					'**`i18n` is required for any editable text:** Whenever you send **`html`** that contains **`data-i18n-field`**, '
					+ 'you **must**'
				),
				(
					'include **`i18n`** on the **same** section row: `fieldId -> { localeCode: string }` covering **every** '
					+ 'field id used in that HTML.'
				),
				'Use **locale codes** from **`websiteLocales`** / **`editorSelectedLocale`** in **currentState** (e.g. `en`, `fr`).',
				(
					'Without **`i18n`**, the hub never receives the strings: users cannot update that copy in the editor and the site '
					+ 'will not resolve or show text correctly.'
				),
				'Omit **`i18n`** only when you omit **`html`** on that row (e.g. patch **`agentCss`** or reorder only).',
				(
					'**Merge:** `i18n` is merged into existing section i18n (partial updates add/override fields only; '
					+ 'they do not wipe other locales).'
				),
				'**`i18n: null`** clears all stored strings for that section.',
				(
					'If you only have copy for one locale, still send **`i18n`** with that locale key; use **currentState** to fill '
					+ 'other **websiteLocales** when possible.'
				),
				'In HTML, mark nodes with **`data-i18n-field="<fieldId>"`**',
				'(and optional **`data-i18n-rich="true"`** for markdown body) so the preview wires click-to-edit.',
				'For **images** the user can replace later in the visual editor, use ',
				'**`<img data-i18n-image="<fieldId>" src="…" alt="…">`**',
				'(same `fieldId` rules as text). The `src` is the default picture (any URL); after upload, the draft image wins.',
				'Image slugs in the hub are **`agent-<sectionId>-<fieldId>`** ',
				'(same pattern as text keys under `texts.`, without the category).',
				(
					'**CTAs (lean, not elastic buttons):** URL is always a second i18n key **`<base>-href`** (same as hub action buttons). '
					+ '**(1) Simple:** `<a data-i18n-field="ctaBook" data-i18n-cta href="#">…</a>` — no nested `data-i18n-field` in `<a>`; '
					+ 'include **`i18n.ctaBook`** and **`i18n["ctaBook-href"]`** per locale. '
					+ '**(2) Large tile:** `<a data-i18n-href-field="tileLink" href="#">…</a>` with inner **`data-i18n-field`**; '
					+ 'include **`i18n["tileLink-href"]`** (optional empty **`i18n.tileLink`** for Action dialog label). '
					+ 'Preview: **`target="_blank"`** only for `http`/`https`; `#`, `tel:`, `mailto:`, internal paths unchanged.'
				),
				'**Ordering:** non–agent-html elastic sections stay fixed; agent-html blocks are appended after them in **final** order.',
				'**Section list semantics:** The editor **merges** partial payloads so other agent-html blocks are not wiped by mistake.',
				'If **every** `id` you send already exists on the page as agent-html, omitted ids are **kept** (patch + preserve).',
				'If your array lists the **same id multiset** as the page (reorder), that order is used.',
				'If **every** existing agent-html id appears in your array and you add **new** ids, ',
				'new blocks are **appended** after existing.',
				'If you add **new** ids but omit some existing agent-html ids, omitted blocks are **still kept**',
				'(incoming order first, then any not-yet-listed blocks in their previous order).',
				'If **every** incoming `id` is new (no overlap with existing agent-html slugs), those blocks are **appended**',
				(
					'after existing agent-html sections — when adding a section with **`html`** that uses **`data-i18n-field`**, '
					+ 'always send **`i18n`** too'
				),
				'(same row), not only `{ id, html, agentCss? }`.',
				(
					'**Placement:** optional **`afterSectionId`** on a row (must be another agent-html **id** already on the page '
					+ 'or in the same'
				),
				'`sections` batch) moves that row immediately after that id in the final agent-html order.',
				(
					'**`agentSectionsOrder`:** optional **JSON array string** of every agent-html section **id** in desired order '
					+ '(id-only reorder,'
				),
				'low token cost). When sent **with** `sections`, it must list the **same** multiset of ids as the result of that update.',
				'May be sent **without** `sections` to reorder only (omit `stylesheet` to leave global CSS unchanged).',
				'Set **`sectionsReplaceAll`: true** only when you intend to **replace the entire** agent-html stack with the',
				'given `sections` list as the sole source of truth (rare). **Empty `sections` (`[]`):** removes all agent-html blocks.',
				'You must send at least one of: **`sections`**, **`stylesheet`**, or **`agentSectionsOrder`**.',
				'Section `id` values must be unique in `sections`, lowercase slugs matching /^[a-z][a-z0-9-_]+$/ (no dots).',
				'Prefer **website_page_get_state** first; after this tool runs, rely on the next turn’s **currentState** snapshot.',
				'Hub **websitePrimaryColor** / **websiteSecondaryColor** are in **currentState**',
				'(from `website_page_get_state` / client snapshot) —',
				'use them in `stylesheet` for brand-aligned UI unless the user overrides.',
				'**Tool args shape:** pass **`sections` as one JSON string** (see schema), not an array of nested objects in the API —',
				'that avoids tool-schema parse failures when listing many blocks.',
			].join(' '),
			schema: {
				stylesheet: {
					type: 'string',
					description:
						'Optional. Full CSS for `page.stylesheet`. Omit the key entirely to keep existing page CSS.',
					required: false,
				},
				sections: {
					type: 'string',
					description: [
						'Optional. **JSON array string** of section objects,',
						(
							'e.g. `[{"id":"hero","html":"<section>…</section>",'
							+ '"agentCss":".agent-html-scope--hero .title { font-size: 1.5rem; }",'
							+ '"i18n":{"title":{"en":"Welcome","fr":"Bienvenue"}}}]`.'
						),
						'Each object: **`id`** (slug), optional **`html`**, optional **`agentCss`** (string; omit key to leave unchanged;',
						'`null` clears), optional **`afterSectionId`** (agent-html id to follow),',
						(
							'**`i18n`** (JSON object or string: fieldId→locale→text): **required on any row where `html` '
							+ 'includes `data-i18n-field`** —'
						),
						(
							'and **whenever** the HTML uses **`data-i18n-cta`** or **`data-i18n-href-field`**, '
							+ 'include matching **`*-href`** i18n keys for URLs (e.g. `ctaBook-href`, `tileLink-href`).'
						),
						(
							'must list **every** such field id with strings for **websiteLocales** from **currentState**; '
							+ 'omit **`i18n`** only when **`html`** is omitted on that row.'
						),
						'Omit this property entirely to leave agent-html blocks unchanged; use `"[]"` to remove all agent-html blocks.',
						'Must be **valid JSON**: every section is one `{...}` object inside the array —',
						'never interleave bare strings or key names.',
					].join(' '),
					required: false,
				},
				sectionsReplaceAll: {
					type: 'boolean',
					description: [
						'Optional. If **true**, `sections` is the complete ordered list of agent-html blocks ',
						'(omit to merge/append safely).',
					].join(''),
					required: false,
				},
				agentSectionsOrder: {
					type: 'string',
					description: [
						'Optional. **JSON array string** of agent-html section **ids** in final order, e.g. `["hero","promo","footer"]`.',
						'Use for **id-only reorder** (minimal tokens) or to set order after a `sections` patch.',
						'Must list **every** agent-html id exactly once after the update (same multiset as the page).',
					].join(' '),
					required: false,
				},
			},
			async execute(args) {
				const api = getApi()
				if (!api) {
					return { applied: false, error: 'editor_not_ready' }
				}
				const raw = args || {}
				const hasStylesheet = Object.prototype.hasOwnProperty.call(raw, 'stylesheet')
				const hasSections = Object.prototype.hasOwnProperty.call(raw, 'sections')
				const hasAgentSectionsOrder = Object.prototype.hasOwnProperty.call(raw, 'agentSectionsOrder')
				if (!hasStylesheet && !hasSections && !hasAgentSectionsOrder) {
					return { applied: false, reason: 'nothing_to_update' }
				}

				const { stylesheet } = raw
				let { sections } = raw
				if (hasSections) {
					if (typeof sections === 'string') {
						try {
							sections = JSON.parse(sections)
						} catch (_e) {
							return { applied: false, reason: 'sections_invalid_json' }
						}
					}
					// Orchestrator / callers may still send a native array (legacy or tests).
					if (!Array.isArray(sections)) {
						return { applied: false, reason: 'sections_must_be_array' }
					}
					for (let si = 0; si < sections.length; si += 1) {
						let row = sections[si]
						if (!row || typeof row !== 'object' || Array.isArray(row)) {
							return {
								applied: false,
								reason: 'sections_entry_must_be_object',
								index: si,
							}
						}
						if (row.i18n != null && typeof row.i18n === 'string') {
							try {
								row.i18n = JSON.parse(row.i18n)
							} catch (_e) {
								return { applied: false, reason: 'i18n_invalid_json', sectionId: row.id }
							}
						}
						row = normalizeAgentSyncSectionRow(row)
						sections[si] = row
					}
				}

				const payload = {}
				if (hasStylesheet) {
					payload.stylesheet = stylesheet
				}
				if (hasSections) {
					payload.sections = sections
				}
				if (Object.prototype.hasOwnProperty.call(raw, 'sectionsReplaceAll')) {
					payload.sectionsReplaceAll = raw.sectionsReplaceAll === true
				}
				if (hasAgentSectionsOrder) {
					payload.agentSectionsOrder = raw.agentSectionsOrder
				}

				const result = api.syncAgentHtmlLayout(payload)
				if (result && result.ok === false) {
					return { applied: false, ...result }
				}
				return {
					applied: true,
					elasticSectionOrder: result && result.elasticSectionOrder,
					agentSectionIds: result && result.agentSectionIds,
				}
			},
		},
	]
}
